import React, { ReactNode } from 'react';
import { ModalBackdrop, ModalContainer, ModalContent } from 'is24-corecss';
import { css } from '@emotion/css';

import { Tabs } from '../tabs/Tabs';
import { GRAY300, GRAY900 } from '../../../../domain/colors';
import { Benefit } from '../../../../@types/benefits';
import { ImageSource, WebPImageElement } from '../imageSource/ImageSource';
import './Modal.css';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

const styles = {
  container: css`
    max-width: 1000px;
    width: 100% !important;
    overflow-x: hidden;
    @media (max-width: 668px) {
      position: fixed !important;
      overflow-y: scroll;
      display: flex;
      flex-flow: column;
      top: 0;
    }
    @media (orientation: landscape) and (max-width: 1023px) {
      height: 100vh;
    },
  `,
  modalContentWrapper: css`
    max-height: inherit !important;
    overflow: hidden !important;
    display: flex;
    flex-flow: column;
    @media (min-width: 668px) {
      max-height: calc(100vh - 200px) !important;
    }
  `,
  modalContent: css`
    overflow-y: auto;
    overflow-x: hidden;
  `,
  imageWrapper: {
    backgroundColor: 'white',
    position: 'relative',
    height: 320,
    margin: '-24px -32px 2px -32px',
    borderBottom: `1px solid ${GRAY300}`,
    backgroundRepeat: 'no-repeat',
    overflow: 'hidden',
    '@media (max-width: 668px)': {
      margin: '-16px -32px 2px -32px',
    },
  },
  image: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    fit: {
      contain: {
        objectFit: 'contain',
      },
      cover: {
        objectFit: 'cover',
      },
    },
    margin: '0 auto',
    display: 'block',
  },
  contentWrapper: {
    withButton: {
      '@media (max-width: 1013px)': {
        marginBottom: 72,
      },
    },
    '& [class*="tabs-container"]': {
      '@media (max-width: 668px)': {
        marginBottom: 4,
      },
    },
    '& [role="tablist"]': {
      zIndex: 5,
      justifyContent: 'center',
      top: -2,
      padding: '20px 0 24px 0',
      '@media (max-width: 668px)': {
        padding: '20px 0 16px 0',
        marginBottom: -12,
      },
      '@media (min-width: 669px)': {
        position: 'sticky',
      },
    },
  },
  separator: {
    marginLeft: -32,
    marginRight: -32,
    '@media (max-width: 1013px)': {
      position: 'fixed',
      zIndex: 6,
      bottom: 0,
      left: 0,
      right: 0,
      boxShadow: `0px 0px 20px -10px ${GRAY900}`,
    },
  },
  buttonWrapper: {
    background: `var(--color-background-default)`,
    maxWidth: 768,
    '@media (orientation: landscape) and (max-width: 1023px)': {
      paddingBottom: 24,
    },
  },
  modalBackDrop: css`
    @media (max-width: 669px) {
      position: initial !important;
    }
  `,
} as const;

export type ModalProps = {
  title: string | ReactNode;
  isVisible: boolean;
  image: string;
  imageWebp?: WebPImageElement;
  imageMobile?: string;
  imageMobileWebp?: WebPImageElement;
  video?: string;
  imageUseFullWidth: boolean;
  imageAlt: string;
  imageBackgroundStyles?: object;
  tabsContent: [JSX.Element, JSX.Element?, JSX.Element?, JSX.Element?];
  buttonArea?: JSX.Element | null;
  formData?: JSX.Element | null;
  benefit: Benefit;
  onClose: () => void;
};

export const Modal = ({
  title,
  isVisible,
  image,
  imageWebp,
  imageMobile,
  imageMobileWebp,
  video,
  imageUseFullWidth,
  imageAlt,
  imageBackgroundStyles,
  tabsContent,
  buttonArea,
  benefit,
  formData,
  onClose,
}: ModalProps) => {
  const camelCasedContentIdentifier = `_${benefit.toLowerCase()}ModalContent`.replace(/_./g, (match) =>
    match[1].toUpperCase(),
  );
  useElementDarkMode('[data-testid="modal-backdrop"]', isVisible);
  useBodyScroll(isVisible);
  useModalFocus(isVisible);

  return (
    <ModalBackdrop visible={isVisible} onClose={onClose} className={css(styles.modalBackDrop)}>
      <ModalContainer
        containerClassName={`Modal ${css(styles.container)} ${camelCasedContentIdentifier}`}
        contentWrapperClassName={`absolute-reference ${css(styles.modalContentWrapper)}`}
        visible={isVisible}
        modalTitle={title}
        onClose={onClose}
      >
        <ModalContent className={`${css(styles.modalContent)}`}>
          <div>
            {!formData && (
              <div data-testid='ImageWrapper' className={css({ ...styles.imageWrapper, ...imageBackgroundStyles })}>
                {video ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    aria-label={imageAlt}
                    style={{
                      ...(imageUseFullWidth ? styles.image.fit.cover : styles.image.fit.contain),
                      ...styles.image,
                    }}
                  >
                    <source src={video} type='video/webm' />
                  </video>
                ) : (
                  <ImageSource
                    image={image}
                    imageWebp={imageWebp}
                    imageMobile={imageMobile}
                    imageMobileWebp={imageMobileWebp}
                    imageAlt={imageAlt}
                    sizes='(min-width: 1014px) 1000px, 100vw'
                    style={{
                      ...(imageUseFullWidth ? styles.image.fit.cover : styles.image.fit.contain),
                      ...styles.image,
                    }}
                  />
                )}
              </div>
            )}
            <div className={`${css(styles.contentWrapper, buttonArea && styles.contentWrapper.withButton)}`}>
              {formData ? formData : <Tabs tabsContent={tabsContent} benefit={benefit} />}
            </div>
          </div>
        </ModalContent>
        {!!buttonArea && (
          <div className={css(styles.separator)}>
            <div
              className={`horizontal-center palm-padding-bottom-l lap-padding-bottom-l padding-top-l padding-bottom-l ${css(styles.buttonWrapper)}`}
            >
              {buttonArea}
            </div>
          </div>
        )}
      </ModalContainer>
    </ModalBackdrop>
  );
};
